import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import apiService from '../../services/api';

interface ManufacturerComboboxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

interface ManufacturerRow {
  id: number;
  name: string;
}

function ManufacturerCombobox({ value, onChange, placeholder = 'e.g. Bayer CropScience' }: ManufacturerComboboxProps) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<ManufacturerRow[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep query in sync if parent resets value (e.g. edit load)
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiService.get<ManufacturerRow[]>(`/spray/manufacturers?q=${encodeURIComponent(query)}`);
        if (res.success && res.data) setSuggestions(res.data);
      } catch {
        // silently ignore
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (name: string) => {
    setQuery(name);
    onChange(name);
    setSuggestions([]);
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setOpen(true);
  };

  const exactMatch = suggestions.some(s => s.name.toLowerCase() === query.trim().toLowerCase());
  const showAddNew = query.trim().length > 0 && !exactMatch && !loading;
  const showDropdown = open && (suggestions.length > 0 || showAddNew);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <ChevronDown className="absolute right-2.5 top-2.5 h-4 w-4 text-neutral-400 pointer-events-none" />
      </div>

      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {suggestions.map(s => (
            <button
              key={s.id}
              type="button"
              onMouseDown={() => handleSelect(s.name)}
              className="w-full text-left px-3 py-2 text-sm text-neutral-800 hover:bg-primary-50 hover:text-primary-700"
            >
              {s.name}
            </button>
          ))}

          {showAddNew && (
            <button
              type="button"
              onMouseDown={() => handleSelect(query.trim())}
              className="w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 flex items-center gap-1.5 border-t border-neutral-100"
            >
              <Plus className="h-3.5 w-3.5" />
              Add "<span className="font-medium">{query.trim()}</span>"
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ManufacturerCombobox;
